import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import type { AuthState, FirebaseUser, PartnerClaims } from '../types/auth'
import { onAuthChanged, getPartnerClaims, signIn, signOutUser } from '../services/authService'

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [claims, setClaims] = useState<PartnerClaims | null>(null)
  const [claimsLoading, setClaimsLoading] = useState(false)

  useEffect(() => {
    const unsub = onAuthChanged(async (firebaseUser: any) => {
      setUser(firebaseUser as FirebaseUser | null)
      if (firebaseUser) {
        setClaimsLoading(true)
        try {
          const c = await getPartnerClaims(firebaseUser)
          setClaims(c)
        } catch {
          setClaims(null)
        } finally {
          setClaimsLoading(false)
        }
      } else {
        setClaims(null)
      }
      setAuthLoading(false)
    })
    return unsub
  }, [])

  const signInWithEmailPassword = useCallback(async (email: string, password: string) => {
    const firebaseUser = await signIn(email, password)
    const c = await getPartnerClaims(firebaseUser)
    if (c.role !== 'partner' || !c.distributorId) {
      await signOutUser()
      throw new Error('Acesso restrito a parceiros OnlyWine.')
    }
    setClaims(c)
    setUser(firebaseUser as unknown as FirebaseUser)
  }, [])

  const handleSignOut = useCallback(async () => {
    await signOutUser()
    setUser(null)
    setClaims(null)
  }, [])

  const value: AuthState = {
    user,
    authLoading,
    claims,
    claimsLoading,
    signInWithEmailPassword,
    signOut: handleSignOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider')
  }
  return ctx
}
